import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { InvoiceEditData } from "../../../hooks/useInvoiceEdit";
import { useInvoicesStore } from "../../../stores/invoicesStore";
import { createEmptyInvoiceData } from "../../../utils/documentDefaults";
import { InvoiceTemplateRouter } from '@/components/templates/invoice/InvoiceTemplateRouter';
import { InvoiceTemplateEditRouter } from '@/components/templates/invoice/editable/InvoiceTemplateEditRouter';
import { Button } from '@/components/ui';
import type { InvoiceWithRelations, InvoiceItem } from '../types/billing.types';

// Wrapper pour créer une nouvelle facture
export const InvoiceNewRoute: React.FC = () => {
  const emptyInvoiceData = createEmptyInvoiceData();

  return <InvoiceTemplateEditRouter initialData={emptyInvoiceData} />;
};

// Wrapper pour éditer une facture existante
export const InvoiceEditRoute: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // TODO: Migrer vers useQuery (convention TanStack Query) — Zustand profondément intégré ici (fetch + state + loading + error)
  const { currentInvoice, isLoading, error, fetchInvoice } = useInvoicesStore();

  // Charger la facture si nécessaire
  React.useEffect(() => {
    if (id && id !== "0") {
      fetchInvoice(id);
    }
  }, [id, fetchInvoice]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="bg-white/60 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/40 p-8">
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600"></div>
            <p className="text-gray-700 font-medium">{t('invoices.list.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !currentInvoice) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-6 text-center">
          <p className="text-red-600 font-semibold mb-4">{t('invoices.notFound', 'Facture introuvable')}</p>
          <Button onClick={() => navigate("/invoices")}>
            {t('invoices.backToList', 'Retour à la liste')}
          </Button>
        </div>
      </div>
    );
  }

  // Convertir la facture pour InvoiceEditData (nouveau format)
  // Cast vers InvoiceWithRelations car l'API retourne les relations Client, Company, InvoiceItem
  const ci = currentInvoice as unknown as InvoiceWithRelations;
  const rawItems =
    typeof ci.items === "string" ? JSON.parse(ci.items) : (ci.items || ci.InvoiceItem || []);

  // Normaliser les items pour avoir tous les champs requis
  const normalizedItems = (rawItems as InvoiceItem[]).map((item) => ({
    id: item.id || crypto.randomUUID(),
    description: item.description || "",
    quantity: item.quantity || 0,
    unitPrice: item.unitPrice || 0,
    taxPercent: item.taxPercent ?? 0,
    total: item.totalHT || 0,
    unit: item.unit || undefined,
    reference: item.reference || undefined,
    lineType: item.lineType || 'ITEM' as const,
    groupId: item.groupId || undefined,
    groupTitle: item.groupTitle || undefined,
    discountType: item.discountType || undefined,
    discountValue: item.discountValue ? Number(item.discountValue) : undefined,
    discountAmount: item.discountAmount ? Number(item.discountAmount) : undefined,
    taxRateId: item.taxRateId || undefined,
    productId: item.productId || undefined,
    descriptionType: item.descriptionType || 'COMMERCIAL',
  }));

  const editData: InvoiceEditData = {
    id: ci.id,
    invoiceNumber: ci.number,
    date: String(ci.issueDate || ''),
    dueDate: String(ci.dueDate || ''),

    // Optional fields
    reference: ci.reference || "",

    // ✅ CRITIQUE: clientId obligatoire pour la mise à jour
    clientId: ci.clientId,

    // Client - Utilise UNIQUEMENT la structure Prisma V2 (pas de fallback legacy)
    clientName: ci.Client?.fullName || ci.Client?.companyName || "",
    clientCompany: ci.Client?.companyName || "",
    clientEmail: ci.Client?.email || "",
    clientAddressLine1: ci.Client?.addressLine1 || "",
    clientCity: ci.Client?.city || "",
    clientZipCode: ci.Client?.zipCode || "",
    clientCountry: ci.Client?.countryCode || "FR",
    clientPhone: ci.Client?.phone || "",
    clientVatNumber: ci.Client?.vatNumber || "",

    // Items et totaux
    items: normalizedItems,
    subtotal: parseFloat(String(ci.totalHT)) || 0,
    tax: parseFloat(String(ci.totalTVA)) || 0,
    total: parseFloat(String(ci.totalTTC)) || 0,

    // Remise
    discountType: ((ci.discountType || "NONE").toLowerCase()) as
      | "none"
      | "percentage"
      | "fixed",
    discountValue: parseFloat(String(ci.discountValue)) || 0,
    discountAmount: parseFloat(String(ci.discountAmount)) || 0,

    // Notes & Titre (alignés sur Prisma)
    title: ci.title || "",
    internalNotes: ci.internalNotes || "",
    termsConditions: ci.termsConditions || "",

    // Company data from relation
    companyName: ci.Company?.legalName || ci.Company?.tradeName || "",
    companyAddressLine1: ci.Company?.addressLine1 || "",
    companyCity: ci.Company?.city || "",
    companyZipCode: ci.Company?.zipCode || "",
    companyCountry: ci.Company?.countryCode || "FR",
    companyPhone: ci.Company?.phone || "",
    companyEmail: ci.Company?.email || "",
    companySiret: ci.Company?.registrationNumber || "",
    companyVatNumber: ci.Company?.vatNumber || "",

    // Paiement (obligatoire pour Factur-X EN 16931)
    paymentMethod: ci.paymentMethod || "BANK_TRANSFER",
  };

  return <InvoiceTemplateEditRouter initialData={editData} />;
};

// Wrapper pour voir une facture en lecture seule
export const InvoiceViewRoute: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // TODO: Migrer vers useQuery (convention TanStack Query) — Zustand profondément intégré ici (fetch + state + loading + error)
  const { currentInvoice, isLoading, error, fetchInvoice } = useInvoicesStore();

  // Charger la facture si nécessaire
  React.useEffect(() => {
    if (id && id !== "0") {
      fetchInvoice(id);
    }
  }, [id, fetchInvoice]);

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">{t('common.loading')}</p>
      </div>
    );
  }

  if (error || !currentInvoice) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">{t('invoices.notFound', 'Facture introuvable')}</p>
        <Button onClick={() => navigate("/invoices")} className="mt-4">
          {t('invoices.backToList', 'Retour à la liste')}
        </Button>
      </div>
    );
  }

  // Transformer Invoice (Prisma) en InvoiceData (template)
  // Cast vers InvoiceWithRelations car l'API retourne les relations Client et InvoiceItem
  const ci = currentInvoice as unknown as InvoiceWithRelations;
  const invoiceData = {
    id: ci.id,
    number: ci.number || '',
    invoiceNumber: ci.number || '',
    issueDate: String(ci.issueDate || ''),
    date: String(ci.issueDate || ''),
    dueDate: String(ci.dueDate || ''),
    title: ci.title || '',
    // Passer l'objet Client complet (attendu par les templates)
    Client: ci.Client,
    // Aussi garder les propriétés plates pour compatibilité
    clientName: ci.Client?.fullName || '',
    clientCompany: ci.Client?.companyName || '',
    clientAddressLine1: ci.Client?.addressLine1 || '',
    clientAddressLine2: ci.Client?.addressLine2 || '',
    clientCity: ci.Client?.city || '',
    clientZipCode: ci.Client?.zipCode || '',
    clientCountryCode: ci.Client?.countryCode || 'FR',
    clientPhone: ci.Client?.phone || '',
    clientEmail: ci.Client?.email || '',
    items: ci.InvoiceItem?.map((item: InvoiceItem) => ({
      description: item.description || '',
      quantity: item.quantity || 0,
      unitPrice: item.unitPrice || 0,
      total: item.totalHT || 0,
      taxPercent: item.taxPercent || 0,
      unit: item.unit || undefined,
      reference: item.reference || undefined,
      lineType: item.lineType || 'ITEM',
      groupId: item.groupId || undefined,
      groupTitle: item.groupTitle || undefined,
      descriptionType: item.descriptionType || 'COMMERCIAL',
      technicalDescription: (item as any).Product?.technicalDescription || undefined,
      discountType: (item.discountType as any) || 'NONE',
      discountValue: item.discountValue || 0,
      discountAmount: item.discountAmount || 0,
    })) || [],
    subtotal: ci.totalHT || 0,
    totalHT: ci.totalHT || 0,
    tax: ci.totalTVA || 0,
    totalTVA: ci.totalTVA || 0,
    total: ci.totalTTC || 0,
    totalTTC: ci.totalTTC || 0,
    discountType: ci.discountType || 'none',
    discountValue: ci.discountValue || 0,
    discountAmount: ci.discountAmount || 0,
    internalNotes: ci.internalNotes || '',
    notes: ci.internalNotes || '',
    termsConditions: ci.termsConditions || '',
    paymentConditions: ci.termsConditions || '',
    status: ci.status || '',
    paidAmount: ci.paidAmount || 0,
    remainingAmount: ci.remainingAmount || 0,
    reference: ci.reference || '',
  };

  return (
    <InvoiceTemplateRouter
      invoiceData={invoiceData}
      onClose={() => {
        navigate("/invoices");
      }}
    />
  );
};
